import React from "react";
import { Switch } from "components/ui/switch";
import { getBomDistributorSummary } from "utils/bomToolUtils";

const PipelineChip = ({ tone, label, value }) => {
  const map = {
    total: "border-t-s360-violet",
    mpn: "border-t-s360-danger",
    stock: "border-t-s360-warning",
    multiple: "border-t-s360-violet-300",
    partial: "border-t-s360-info",
  };
  return (
    <div className={`bg-white rounded-md shadow-sm px-4 py-3 border-t-[3px] ${map[tone]} flex flex-col gap-0.5`}>
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3">
        {label}
      </span>
      <span className="text-[22px] leading-7 font-bold tracking-[-0.015em] text-s360-ink tabular-nums">
        {value ?? "-"}
      </span>
    </div>
  );
};

const BomStatsView = ({
  bomStats,
  bomItems,
  distributorOnlySummary,
  isDistributorSelected,
  toggleDistributor,
}) => {
  if (!bomStats) return null;

  const {
    totalBomCount,
    mpnNotFoundCount,
    stockNotFoundCount,
    multipleMpnFoundCount,
    partialQtyCount,
  } = bomStats;

  const distributorSummary = getBomDistributorSummary(bomItems || []);
  const grandTotal = distributorSummary.reduce((sum, d) => sum + d.totalPrice, 0);
  const grandCurrency = distributorSummary[0]?.currency ?? "";

  const totalItemCount = (distributorOnlySummary || []).reduce(
    (sum, d) => sum + d.availableCount + d.unavailableCount,
    0,
  );

  // "Cheapest single" only considers distributors that are currently enabled
  // via the comparison toggles — disabling one re-picks among the rest.
  const enabledSummary = (distributorOnlySummary || []).filter((d) =>
    isDistributorSelected ? isDistributorSelected(d.distributor) : true,
  );
  const cheapestSingle =
    enabledSummary.length > 0
      ? Math.min(...enabledSummary.map((d) => d.totalPrice))
      : Infinity;

  return (
    <div className="flex flex-col gap-4">
      {/* Pipeline chips */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <PipelineChip tone="total" label="Total parts" value={totalBomCount} />
        <PipelineChip tone="partial" label="Partial qty" value={partialQtyCount} />
        <PipelineChip tone="stock" label="No Stock" value={stockNotFoundCount} />
        <PipelineChip tone="multiple" label="Multiple MPN" value={multipleMpnFoundCount} />
        <PipelineChip tone="mpn" label="Invalid MPN" value={mpnNotFoundCount} />

      </div>

      {(distributorSummary.length > 0 || distributorOnlySummary.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          {/* Optimal hero */}
          {distributorSummary.length > 0 && (
            <div className="rounded-md shadow-sm overflow-hidden bg-gradient-to-br from-s360-violet to-s360-violet-900 text-white p-5 flex flex-col gap-3">
              <div className="flex items-center gap-2 text-[13px] font-semibold text-white/85">
                <span className="w-1.5 h-1.5 rounded-full bg-s360-teal" />
                Optimized mix · best price across distributors
              </div>
              <div className="font-mono text-[32px] leading-9 font-bold tabular-nums">
                {grandCurrency} {grandTotal.toFixed(2)}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
                {distributorSummary.map((d) => (
                  <div key={d.distributor} className="bg-white/10 rounded-md px-3 py-2 flex flex-col gap-0.5">
                    <span className="text-[12px] font-semibold truncate">{d.distributor}</span>
                    <span className="font-mono text-[13px] tabular-nums">
                      {d.currency} {d.totalPrice.toFixed(2)}
                    </span>
                    <span className="text-[11px] text-white/70">
                      {d.itemCount} item{d.itemCount !== 1 ? "s" : ""}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Single distributor comparison */}
          {distributorOnlySummary.length > 0 && (
            <div className="bg-white rounded-md shadow-sm flex flex-col">
              <div className="px-[18px] py-3.5 border-b border-s360-stroke1">
                <h3 className="text-base font-semibold text-s360-fg1 m-0">
                  Single distributor comparison
                </h3>
                <p className="text-xs text-s360-fg3 mt-0.5">
                  Estimated total if all {totalItemCount} items were sourced from one supplier
                </p>
              </div>
              <div className="flex flex-col divide-y divide-s360-bg2">
                {distributorOnlySummary.map((d) => {
                  const on = isDistributorSelected ? isDistributorSelected(d.distributor) : true;
                  const isBest = on && d.totalPrice === cheapestSingle;
                  const extra = grandTotal > 0 ? d.totalPrice - grandTotal : 0;
                  return (
                    <div
                      key={d.distributor}
                      className={`flex items-center gap-3 px-[18px] py-2.5 ${
                        isBest ? "bg-s360-success-bg/50" : ""
                      } ${on ? "" : "opacity-55"}`}
                    >
                      {toggleDistributor && (
                        <Switch
                          checked={on}
                          onClick={() => toggleDistributor(d.distributor)}
                          className="data-[state=checked]:bg-s360-violet data-[state=unchecked]:bg-s360-bg3 shrink-0"
                        />
                      )}
                      <span className="text-sm font-semibold text-s360-fg1 truncate">
                        {d.distributor}
                      </span>
                      {isBest && (
                        <span className="text-[10px] font-bold uppercase tracking-[0.06em] bg-s360-success-bg text-s360-success-ink px-1.5 py-0.5 rounded shrink-0">
                          cheapest single
                        </span>
                      )}
                      <span className="ml-auto flex items-center gap-2.5 shrink-0">
                        <span className="font-mono text-[13px] font-bold text-s360-ink tabular-nums">
                          {d.currency} {d.totalPrice.toFixed(2)}
                        </span>
                        {extra > 0 && (
                          <span className="text-xs whitespace-nowrap">
                            <span className="text-s360-warning font-semibold">
                              +{d.currency} {extra.toFixed(2)}
                            </span>{" "}
                            <span className="text-s360-fg3">vs optimal</span>
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BomStatsView;
